<template>
  <div>
    <el-table
      ref="el-table"
      class="table-container"
      :class="{ outlined: outline }"
      v-loading="isLoading"
      :data="data"
      :stripe="true"
      :row-key="rowKey"
      :lazy="lazy"
      :load="load"
      :tree-props="treeProps"
      :border="border"
      :show-header="showHeader"
      :empty-text="listEmptyText"
      :max-height="maxHeight"
      @row-click="rowClick"
      @selection-change="selectionChange"
      @sort-change="sortChange"
      :default-sort="defaultSort"
      :row-class-name="tableRowClassName"
      :qa-loaded="qaLoaded"
    >
      <slot />
    </el-table>
    <el-pagination
      v-if="pagination && maxPages > 1"
      background
      class="paginator"
      :layout="paginationLayout"
      :page-count="maxPages"
      :current-page="currentPage"
      :disabled="isLoading"
      @current-change="pageChange"
    >
      <template>
        <ul class="el-pager">
          <li class="number active">{{ currentPage }}</li>
        </ul>
      </template>
    </el-pagination>
  </div>
</template>

<script>
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import qaLoadedMixin from '@/common/mixins/qaLoadedFlag'

export default {
  name: 'RestList',
  mixins: [qaLoadedMixin],
  props: {
    topData: {
      type: Array,
      required: false
    },
    payload: {
      required: true,
      type: Object
    },
    watchPayload: {
      type: Boolean,
      default: true
    },
    afterFetch: {
      type: Function
    },
    border: {
      type: Boolean,
      default: true
    },
    outline: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      required: false
    },
    filterable: {
      type: Boolean,
      default: true
    },
    pagination: {
      type: Boolean,
      default: true
    },
    lazy: {
      type: Boolean,
      default: false
    },
    lazyHandler: {
      type: Function,
      default: () => {
        return true
      }
    },
    load: {
      type: Function,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    treeProps: {
      type: Object,
      default() {
        return {
          hasChildren: 'hasChildren',
          children: 'children'
        }
      }
    },
    rowKey: [String, Function],
    emptyText: {
      type: String,
      default: null
    },
    noFiltersEmptyText: {
      type: String,
      default: null
    },
    applyFiltersToUrl: {
      type: Boolean,
      default: true
    },
    refreshAfterFiltersChanged: {
      type: Boolean,
      default: true
    },
    refreshAfterSortingChanged: {
      type: Boolean,
      default: true
    },
    applySortingToUrl: {
      type: Boolean,
      default: true
    },
    dataHandler: {
      type: Function,
      default: null
    },
    beforePageChange: {
      type: Function,
      default: null
    }
  },

  provide() {
    return {
      restList: this
    }
  },

  data() {
    return {
      data: [],
      editData: {},
      error: null,
      paginator: {},
      maxPages: 1,
      total: 0,
      currentPage: 1,
      paginationLayout: 'prev, pager, next',
      fetching: true,
      filters: {},
      filtersAreEmpty: true,
      defaultSort: {},
      sort: null,
      fetchPageTimeout: null
    }
  },

  mounted() {
    this.paginator = new RestApiPaginator(this.payload)
    if (this.$route.query['sort']) {
      this.setDefaultSort(this.$route.query['sort'])
    }

    /* timeout need to resolve issue with DB lag
    e.g. sometime after deletion of some row, fetch endpoint return data that including already removed row.
    * */

    this.fetchPageTimeout = setTimeout(() => {
      this.fetchPage(1)
    }, 100)
  },

  computed: {
    noDataText() {
      if (this.error) {
        return this.error.message
      } else if (this.emptyText) {
        return this.emptyText
      } else {
        return this.$t('RestList_NoData')
      }
    },

    listEmptyText() {
      if (this.filtersAreEmpty && this.noFiltersEmptyText) {
        return this.noFiltersEmptyText
      }

      return this.noDataText
    },

    isLoading() {
      return this.fetching || this.loading
    }
  },

  methods: {
    tableRowClassName({ row }) {
      if (row.newRow) {
        return 'new-row'
      }
      return ''
    },
    generatePayload() {
      let params = { ...this.payload.params }

      if (this.filters) {
        params = {
          ...params,
          ...this.filters,
          filterFields: Object.keys(this.filters)
        }
      }

      if (this.sort) {
        params.sort = this.sort
      }

      return {
        ...this.payload,
        ...{ params: params }
      }
    },

    filterField(field, val) {
      if (val === null || val.length === 0) {
        delete this.filters[field]
      } else {
        this.filters[field] = val
      }

      this.$emit('filters-change', this.filters)

      if (this.applyFiltersToUrl) {
        this.toggleFiltersInUrl()
      }

      if (this.refreshAfterFiltersChanged) {
        this.refresh(true)
      }

      this.filtersAreEmpty = Object.keys(this.filters || {}).length === 0
    },

    sortChange(sort) {
      let sortBy = sort.column.sortBy ? sort.column.sortBy : sort.prop

      switch (sort.order) {
        case 'descending':
          this.sort = '-' + sortBy
          break
        case 'ascending':
          this.sort = sortBy
          break
        case null:
          this.sort = null
          break
      }

      this.$emit('sort-change', this.sort)

      if (this.applySortingToUrl) {
        this.toggleSortingInUrl()
      }

      if (this.refreshAfterSortingChanged) {
        this.refresh(true)
      }
    },

    async pageChange(val) {
      if (this.beforePageChange) {
        const canProceed = await this.beforePageChange(val)
        if (!canProceed) return
      }
      await this.fetchPage(val)

      this.resetTablePosition()
    },

    async refresh(reset, silent) {
      setTimeout(() => {
        if (reset) {
          this.fetchPage(1, silent)
        } else {
          this.fetchPage(this.currentPage, silent)
        }
      }, 100)
    },

    async fetchPage(page, silent) {
      clearTimeout(this.fetchPageTimeout)
      this.fetchPageTimeout = setTimeout(async () => {
        if (!silent) {
          this.fetching = true
        }

        this.editData = {}

        try {
          this.paginator.setPayload(this.generatePayload())
          let response = await this.paginator.fetchPage(page)

          this.maxPages = Number.parseInt(
            response.headers['x-pagination-page-count']
          )
          this.total = Number.parseInt(
            response.headers['x-pagination-total-count']
          )

          this.currentPage = Number.parseInt(
            response.headers['x-pagination-current-page']
          )

          this.paginationLayout =
            Number.parseInt(response.headers['x-pagination-total-count']) === -1
              ? 'prev, slot, next'
              : 'prev, pager, next'

          if (this.lazy) {
            response.data.forEach(row => {
              row.hasChildren = this.lazyHandler(row)
            })
          }

          this.data = this.dataHandler
            ? this.dataHandler(response.data)
            : response.data

          this.error = null
          this.setQALoaded(true)
          this.$emit('dataLoaded', response.data)
        } catch (error) {
          this.data = null
          this.error = error
          this.$message({
            message: error.message,
            type: 'error'
          })
        } finally {
          this.fetching = false
          if (this.afterFetch) {
            this.afterFetch()
          }
        }
      }, 100)
    },

    resetTablePosition() {
      const headerTable = this.$el.querySelector('.table-container')
      if (headerTable) {
        headerTable.scrollIntoView()
      }
      const contentTable = this.$el.querySelector('.el-table__body-wrapper')
      if (contentTable) {
        contentTable.scrollTop = 0
      }
    },

    rowClick(row, column) {
      this.$emit('row-click', row, column)
    },

    selectionChange(val) {
      this.$emit('selection-change', val)
    },

    isEditingRow(index) {
      return !!this.editData[index]
    },

    isNewRow(index) {
      return this.editData[index].new === true
    },

    addRow(row) {
      this.data.unshift(row)

      const newObject = {}
      Object.keys(this.editData).forEach(key => {
        newObject[parseInt(key) + 1] = this.editData[key]
      })
      this.editData = newObject

      this.editRow(0, true)
    },

    removeRow(index) {
      const newObject = {}
      Object.keys(this.editData).forEach(key => {
        if (parseInt(key) > index) {
          newObject[parseInt(key) - 1] = this.editData[key]
        } else if (parseInt(key) < index) {
          newObject[key] = this.editData[key]
        }
      })
      this.editData = newObject

      this.data.splice(index, 1)
    },

    editRow(index, newRow = false) {
      this.$set(
        this.editData,
        index,
        Object.assign({ new: newRow }, this.data[index])
      )
    },

    editRowData(index) {
      return this.editData[index]
    },

    cancelEditRow(index) {
      this.$set(this.editData, index, undefined)
    },

    updateRow(index, updatedData) {
      this.$set(this.data, index, updatedData)
      this.$set(this.editData, index, undefined)
    },

    toggleFiltersInUrl() {
      this.$router
        .replace({
          query: Object.assign({}, this.$route.query, {
            filters: JSON.stringify(this.filters)
          })
        })
        .catch(() => {})
    },

    toggleSortingInUrl() {
      this.$router.replace({
        query: Object.assign({}, this.$route.query, {
          sort: JSON.stringify(this.sort)
        })
      })
    },

    setDefaultSort(prop) {
      this.sort = prop.replace(/^"(.*)"$/, '$1')
      let srtProp = this.sort.toString().startsWith('-')
        ? this.sort.substring(1)
        : this.sort
      let srtOrder = this.sort.toString().startsWith('-')
        ? 'descending'
        : 'ascending'
      Object.assign(this.defaultSort, { prop: srtProp, order: srtOrder })
      this.$emit('sort-change', this.sort)
    }
  },

  watch: {
    topData: {
      handler() {
        this.data = [...new Set([...this.topData, ...this.data])]
      },
      deep: true
    },

    payload: {
      handler() {
        if (this.watchPayload) {
          this.refresh(true)
        }
      }
    }
  }
}
</script>

<style scoped>
.paginator {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

>>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #1d87c8;
}

>>> .el-pagination.is-background .btn-prev,
>>> .el-pagination.is-background .btn-next,
>>> .el-pagination.is-background .el-pager li {
  background-color: #fff;
}

.table-container.outlined {
  border: 1px solid #dbdee7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

>>> .el-table .caret-wrapper {
  position: absolute;
  left: 2px;
  top: -4px;
}

>>> .el-table .is-sortable .cell > span:first-of-type {
  padding-left: 16px;
}

>>> .el-table-column--selection .cell span {
  padding-left: 2px !important;
}

>>> .el-table-column--selection .cell {
  padding-left: 12px !important;
  text-overflow: clip !important;
}

>>> .table-header {
  vertical-align: top;
}
</style>



// WEBPACK FOOTER //
// src/common/components/rest/RestList.vue